import { useState } from 'react';
import { EFFICIENCY_NAMES } from '@/lib/efficiencyNames'; // CORE-G GAP-005: one name, one meaning
import { PassportSectionFallback } from './PassportSectionFallback';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { 
  Activity, TrendingUp, TrendingDown, Minus, 
  Zap, Shield, Target, Calendar, RotateCcw
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatEfficiencyDelta } from '@/utils/technicianPassportUtils';
import { subDays } from 'date-fns';
import { SafeChart } from '@/components/passport/SafeChart';
import { useDirectTechnicianMetrics, useTechnicianTrendData } from '@/hooks/useDirectTechnicianMetrics';
import { PassportProvenanceBadge } from './PassportProvenanceBadge';
import { useShopTimezone } from '@/contexts/TimezoneContext';
// DTC-K: route the passport performance-DNA "now" anchor through the
// canonical clock so passport-sync analytics windows are deterministic
// under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

interface PassportPerformanceDNAProps {
  technicianProfileId: string;
  liveShopTechnicianId?: string;
  /**
   * CORE-C GAP-005: false while the caller's live-technician lookup is
   * still in flight, so the trend query does not fire once without the id
   * and again with it. Defaults to true for callers that already have it.
   */
  liveIdentityReady?: boolean;
}

type TimeRange = '30d' | '90d';

export function PassportPerformanceDNA({
  technicianProfileId,
  liveShopTechnicianId,
  liveIdentityReady = true,
}: PassportPerformanceDNAProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const { shopTimezone } = useShopTimezone();

  // Use direct metrics from tickets/ticket_jobs (single source of truth)
  const { data: metrics, isError: metricsFailed } = useDirectTechnicianMetrics(technicianProfileId, liveShopTechnicianId);
  // R9-M-5: Pass shopTimezone for correct week bucketing
  const { data: trendData } = useTechnicianTrendData(
    technicianProfileId, timeRange, shopTimezone, liveShopTechnicianId, liveIdentityReady,
  );

  // Fetch comeback counts (30d and 90d)
  const { data: comebackCounts, isError: comebacksFailed } = useQuery({
    queryKey: ['passport-comebacks', technicianProfileId],
    queryFn: async () => {
      let techIds: string[];

      if (liveShopTechnicianId) {
        techIds = [liveShopTechnicianId];
      } else {
        const { data: employment, error: employmentError } = await supabase
          .from('technician_employment_history')
          .select('shop_technician_id')
          .eq('technician_profile_id', technicianProfileId);

        // CORE-C GAP-003: `|| []` on a failed read produced zero technicians,
        // which produced zero comebacks, which the card then coloured green.
        if (employmentError) throw employmentError;
        techIds = employment?.map(e => e.shop_technician_id).filter(Boolean) || [];
      }
      
      if (techIds.length === 0) return { count30d: 0, count90d: 0 };

      const now = clock.now();
      const thirtyDaysAgo = subDays(now, 30);
      const ninetyDaysAgo = subDays(now, 90);

      const { count: count30d, error: count30dError } = await supabase
        .from('tickets')
        .select('*', { count: 'exact', head: true })
        .in('completed_by_tech_id', techIds)
        .eq('is_comeback', true)
        .gte('completed_at', thirtyDaysAgo.toISOString());

      const { count: count90d, error: count90dError } = await supabase
        .from('tickets')
        .select('*', { count: 'exact', head: true })
        .in('completed_by_tech_id', techIds)
        .eq('is_comeback', true)
        .gte('completed_at', ninetyDaysAgo.toISOString());

      // `count || 0` is right for a genuine zero and wrong for a failed count.
      if (count30dError) throw count30dError;
      if (count90dError) throw count90dError;
      return { count30d: count30d || 0, count90d: count90d || 0 };
    }
  });

  // ── CORE-C GAP-003, the sharpest instance in the Passport ───────────────
  //
  // Every figure below is written `x ?? 0` or `x || 0`, so a failed read did
  // not blank this card — it FILLED it. `jobsCompleted` became 0, and
  // `comebackCounts?.count30d || 0` became 0 and was then coloured
  // `text-success`, because the colour rule reads "0 comebacks is green".
  //
  // The product congratulated the person on a comeback rate it had not been
  // able to read. That is a derived claim manufactured from a retrieval
  // failure, and it is the reason this check sits above every computation
  // rather than beside them.
  if (metricsFailed || comebacksFailed) {
    return <PassportSectionFallback sectionName="Performance DNA" />;
  }

  const efficiencyBook = metrics?.avg_efficiency_book ? Math.round(metrics.avg_efficiency_book * 100) : null;
  const comebackRate = metrics?.comeback_rate !== null && metrics?.comeback_rate !== undefined 
    ? (metrics.comeback_rate * 100).toFixed(1) 
    : null;
  const diagnosticAcc = metrics?.diagnostic_accuracy !== null && metrics?.diagnostic_accuracy !== undefined 
    ? Math.round(metrics.diagnostic_accuracy * 100) 
    : null;
  const jobsCompleted = metrics?.jobs_completed || 0;

  // Determine trend direction
  const getTrendIcon = () => {
    if (!trendData || trendData.length < 2) return <Minus className="h-4 w-4 text-muted-foreground" />;
    const recent = trendData.slice(-3);
    const older = trendData.slice(0, 3);
    const recentAvg = recent.reduce((sum, d) => sum + d.efficiency, 0) / recent.length;
    const olderAvg = older.reduce((sum, d) => sum + d.efficiency, 0) / older.length;
    
    if (recentAvg > olderAvg + 2) return <TrendingUp className="h-4 w-4 text-success" />;
    if (recentAvg < olderAvg - 2) return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const efficiencyDelta = metrics?.avg_efficiency_book 
    ? formatEfficiencyDelta(metrics.avg_efficiency_book)
    : null;

  return (
    <Card data-testid="passport-performance">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-muted-foreground" />
            Performance DNA
          </CardTitle>
          <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
            <SelectTrigger className="w-28">
              <Calendar className="h-4 w-4 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30d">30 Days</SelectItem>
              <SelectItem value="90d">90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* Efficiency */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground" title={EFFICIENCY_NAMES.bookTime.scope}>{EFFICIENCY_NAMES.bookTime.label}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {efficiencyBook !== null ? `${efficiencyBook}%` : 'N/A'}
            </p>
            {/* CORE-C GAP-002. Inside the EFFICIENCY tile, not the card header:
                CORE-G declares this one figure a Derivation and says nothing
                about the comeback rate, diagnostic accuracy or job count in the
                sibling tiles. */}
            <div className="mt-1">
              <PassportProvenanceBadge factGroupId="performance-dna-efficiency" />
            </div>
            {efficiencyDelta && (
              <p className={`text-xs mt-1 ${efficiencyDelta.isPositive ? 'text-success' : 'text-destructive'}`}>
                {efficiencyDelta.text}
              </p>
            )}
          </div>

          {/* Comeback Rate */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-success" />
              <span className="text-xs text-muted-foreground">Comeback Rate</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {comebackRate !== null ? `${comebackRate}%` : 'N/A'}
            </p>
            {comebackRate !== null && Number(comebackRate) <= 2 && (
              <Badge variant="outline" className="text-xs mt-1 bg-success/10 text-success border-success/30">
                Low
              </Badge>
            )}
          </div>

          {/* Diagnostic Accuracy */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-accent" />
              <span className="text-xs text-muted-foreground">Diagnostic Acc.</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {diagnosticAcc !== null ? `${diagnosticAcc}%` : 'N/A'}
            </p>
          </div>

          {/* Jobs Completed */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-warning" />
              <span className="text-xs text-muted-foreground">Jobs Completed</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{jobsCompleted}</p>
          </div>

          {/* Comebacks (30d) */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <RotateCcw className="h-4 w-4 text-destructive" />
              <span className="text-xs text-muted-foreground">Comebacks (30d)</span>
            </div>
            <p className={`text-2xl font-bold ${
              (comebackCounts?.count30d || 0) === 0 ? 'text-success' :
              (comebackCounts?.count30d || 0) <= 2 ? 'text-warning' : 'text-destructive'
            }`}>
              {comebackCounts?.count30d || 0}
            </p>
          </div>

          {/* Comebacks (90d) */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <RotateCcw className="h-4 w-4 text-destructive" />
              <span className="text-xs text-muted-foreground">Comebacks (90d)</span>
            </div>
            <p className={`text-2xl font-bold ${
              (comebackCounts?.count90d || 0) === 0 ? 'text-success' :
              (comebackCounts?.count90d || 0) <= 5 ? 'text-warning' : 'text-destructive'
            }`}>
              {comebackCounts?.count90d || 0}
            </p>
          </div>
        </div>

        {/* Speed Insights Chart */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-foreground flex items-center gap-2">
              Speed Insights
              {getTrendIcon()}
            </h4>
          </div>
          
          {trendData && trendData.length >= 2 ? (
            <div className="h-[200px] w-full">
              <SafeChart height="200px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    />
                    <YAxis 
                      domain={[60, 140]}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.5rem',
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="efficiency" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--primary))', r: 3 }}
                      name={`${EFFICIENCY_NAMES.bookTime.label} %`}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </SafeChart>
            </div>
          ) : (
            <div className="h-[200px] w-full bg-muted/30 rounded-lg flex items-center justify-center">
              <p className="text-sm text-muted-foreground">Not enough data for trend chart</p>
            </div>
          )}
          
          <p className="text-xs text-muted-foreground">
            {efficiencyBook && efficiencyBook >= 100 
              ? `Usually completes jobs ${efficiencyBook - 100}% faster than book time. ${trendData && trendData.length > 5 ? 'Stable trend.' : ''}`
              : efficiencyBook 
                ? `Currently running ${100 - efficiencyBook}% slower than book time.`
                : 'Collecting efficiency data...'
            }
          </p>
        </div>

        {/* Quality Insights */}
        <div className="space-y-3">
          <h4 className="font-semibold text-foreground">Quality Insights</h4>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Comeback Rate Bar */}
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Rework Distribution</p>
              <div className="h-4 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full ${
                    Number(comebackRate) <= 2 ? 'bg-success' : 
                    Number(comebackRate) <= 5 ? 'bg-warning' : 'bg-destructive'
                  }`}
                  style={{ width: `${Math.min(Number(comebackRate || 0) * 10, 100)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {Number(comebackRate) <= 2 ? 'Excellent consistency' : 
                 Number(comebackRate) <= 5 ? 'Room for improvement' : 'Needs attention'}
              </p>
            </div>

            {/* Diagnostic Accuracy */}
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Diagnostic Success</p>
              <div className="h-4 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full ${
                    diagnosticAcc && diagnosticAcc >= 80 ? 'bg-success' : 
                    diagnosticAcc && diagnosticAcc >= 60 ? 'bg-primary' : 'bg-warning'
                  }`}
                  style={{ width: `${diagnosticAcc || 0}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {diagnosticAcc && diagnosticAcc >= 80 ? 'Strong diagnostic skills' : 
                 diagnosticAcc && diagnosticAcc >= 60 ? 'Developing accuracy' : 'Building experience'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
